/**
 * 50. Supplier Invoices (Procurement) — capture; match to PO/receipt;
 * three-way matching; approval; payment; duplicate detection.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
  BlockedActionExplanation,
} from "@/components/ui";
import { useTranslation } from "@/lib/i18n";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { formatMoney, formatDate } from "@/lib/money";

interface SupplierInvoiceRow {
  id: string;
  invoiceNumber: string;
  supplierName: string;
  invoiceDate: string;
  dueDate: string | null;
  total: string;
  currencyCode: string;
  status: "Received" | "Matched" | "Approved" | "Posted" | "Paid";
  matchStatus: "NotMatched" | "Matched" | "Exception";
}

export function SupplierInvoicesProcurementPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<SupplierInvoiceRow>(
    ["procurement", "supplier-invoices"],
    "/procurement/supplier-invoices",
  );

  const exceptions = (data?.items ?? []).filter(
    (r) => r.matchStatus === "Exception",
  );

  return (
    <div>
      <PageHeader
        title="Supplier Invoices"
        subtitle="Capture, three-way match against PO + receipt, approve, post, pay."
        actions={
          <PendingButton variant="primary" reason="invoiceEditor" slice="ED-W4">
            {t("documentActions.captureSupplierInvoice")}
          </PendingButton>
        }
      />
      {exceptions.length > 0 && (
        <BlockedActionExplanation
          title="Three-way match exceptions"
          reasons={exceptions.map((r) => ({
            code: "THREE_WAY_MATCH_FAILED",
            message: `Invoice ${r.invoiceNumber} does not match its PO/receipt within tolerance.`,
            resolutionPath:
              "Open the invoice, review quantity/price variances, and either accept with reason or return to supplier.",
          }))}
        />
      )}
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={`Supplier invoices (${data.total})`}>
          <DataTable<SupplierInvoiceRow>
            rows={data.items}
            columns={[
              {
                key: "invoiceNumber",
                label: "Number",
                render: (r) => (
                  <span className="atlas-mono">{r.invoiceNumber}</span>
                ),
              },
              { key: "supplierName", label: "Supplier" },
              {
                key: "invoiceDate",
                label: "Date",
                render: (r) => formatDate(r.invoiceDate),
              },
              {
                key: "dueDate",
                label: "Due",
                render: (r) => (r.dueDate ? formatDate(r.dueDate) : "—"),
              },
              {
                key: "total",
                label: "Total",
                numeric: true,
                render: (r) => formatMoney(r.total, r.currencyCode),
              },
              {
                key: "matchStatus",
                label: "Match",
                render: (r) => (
                  <Badge
                    tone={
                      r.matchStatus === "Matched"
                        ? "success"
                        : r.matchStatus === "Exception"
                          ? "danger"
                          : "warning"
                    }
                  >
                    {r.matchStatus === "NotMatched" ? "Pending" : r.matchStatus}
                  </Badge>
                ),
              },
              {
                key: "status",
                label: "Status",
                render: (r) => <StatusBadge status={r.status} />,
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
